
import { Component, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CustomizerComponentHeaderComponent } from '@app/customizer/components';
import { MenuOverlayComponent } from '@app/shared/components/menu-overlay/menu-overlay';
import { BrandEnum } from '@infrastructure/brand.enum';
import { MiscModel } from '@models/misc-model';
import { TranslatePipe } from '@ngx-translate/core';
import { BrandModeService } from '@services/brandmode.service';
import { ConfigurationService } from '@services/configuration.service';
import { CustomizerService } from '@services/customizer.service';
import { ConfigurationModel } from '@models/configuration';
import { CustomizerMenuPartModel } from '@models/customizer-menu-part';
import { ModelGroupModel } from '@models/model-group';
import { DataApiService } from '@services/data-api.service';
import { EventsService } from '@services/events.service';
import { ComponentVariantModel } from '../../../../_models/component-variant';
import { EngravingService } from '../../../../_services/engraving.service';
import { ComponentCodeConstants } from '../../../../shared/component-constants';
import { ProductlineConstants } from '../../../../shared/productline-constants';
import { BaseCustomizerComponent } from './../../../../shared/base-customizer-component';

@Component({
    selector: 'engraving',
    styleUrls: ['./engraving.scss'],
    template: `
        <customizer-component-header [menu]="menu" (updateCollapsedOnComponent)="updateCollapsed($event)">
          @if (engravingPossible === 0) {
            <div class="has-padding-horizontal-30px">
              {{'CUSTOMIZER.COMPONENTS.ENGRAVING.NOTPOSSIBLE' | translate}}
            </div>
          }
        
          @if (engravingPossible && engravingPossible > 0) {
            <div class="has-padding-horizontal-30px engraving-container">
              <div class="engraving-input customizer-input" [class.has-error]="engravingError">
                @if (showOverlay) {
                  <menu-overlay
                    [mainOverlayText]="null"
                    [secondOverlayText]="'CUSTOMIZER.CHANGES.BAGUPDATED' | translate"
                    (overlayClicked)="null"
                    >
                  </menu-overlay>
                }
                <input class="form-control"
                  [ngModel]="engravingFirst"
                  (ngModelChange)="onUpdateEngraving($event)"
				  (blur)="trimEngraving(engravingFirst, false)"
                  [maxlength]="engravingPossible"
                  type="text"
                  [placeholder]="'CUSTOMIZER.COMPONENTS.ENGRAVING.ENTERUSERNAMEPLACEHOLDER' | translate"
                  >
                  <input class="form-control"
                    [ngModel]="engravingRepeat"
                    (ngModelChange)="onUpdateEngravingRepeat($event)"
					(blur)="trimEngraving(engravingRepeat, true)"
                    [maxlength]="engravingPossible"
                    type="text"
                    [placeholder]="'CUSTOMIZER.COMPONENTS.ENGRAVING.REENTERUSERNAMEPLACEHOLDER' | translate"
                    >
                  </div>
                  <div class="engraving-error">
                    @if (engravingError) {
                      <span>{{engravingError | translate}}</span>
                    }
                  </div>
                  <div class="engraving-instructions">
                    <p>- {{'CUSTOMIZER.COMPONENTS.ENGRAVING.DIRECTIONS1' | translate:{ maxlength: engravingPossible } }}</p>
                    <p>- {{'CUSTOMIZER.COMPONENTS.ENGRAVING.DIRECTIONS2' | translate:{ maxlength: engravingPossible } }}</p>
                  </div>
                  @if (brandMode !== brandEnumType.Precious && ProductlineConstants.SUN !== modelGroup.ProductLineCode && !modelGroup.IsRimless) {
                    <div class="engraving-info">
                      <p>{{'CUSTOMIZER.COMPONENTS.ENGRAVING.INFORMATION' | translate }}</p>
                      <p>{{'CUSTOMIZER.COMPONENTS.ENGRAVING.INFORMATION.RIGHT.OF.RETURN' | translate }}</p>
                    </div>
                  }
                </div>
              }
            </customizer-component-header>
        
        `,
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [TranslatePipe, FormsModule, MenuOverlayComponent, CustomizerComponentHeaderComponent]
})
export class EngravingComponent extends BaseCustomizerComponent {

	brandMode: BrandEnum;
	brandEnumType = BrandEnum;

	constructor(
		public override events: EventsService,
		public apiService: DataApiService,
		private engravingService: EngravingService,
		brandModeService: BrandModeService,
    private custermizerService : CustomizerService,
		public override configurationService: ConfigurationService
	) {
		super(events, apiService, configurationService);
		brandModeService.currentBrand.subscribe(x => this.brandMode = x);
	}

	public engravingPossible?: number;
	public engravingFirst?: string = "";
	public engravingRepeat?: string = "";
	public ProductlineConstants = ProductlineConstants;
	public showOverlay: boolean = false;
	public engravingError: string;

	public static override shouldBeVisible = (modelGroup: ModelGroupModel | undefined, menuComponents: CustomizerMenuPartModel, variants: Array<ComponentVariantModel> | undefined, configurations: Array<ConfigurationModel>, demo: boolean, chosenCase?: MiscModel | null): boolean => {
		if (demo) {
			return false;
		}
		else {
			return EngravingComponent.variantWithEngravingPossible(variants);
		}
	}

	private static variantWithEngravingPossible(variants: Array<ComponentVariantModel> | undefined): boolean {
		if (variants)
			for (let i = 0; i < variants.length; i++) {
				if (variants[i].EngravingPossible) {
					return true;
				}
			}

		return false;
	}

	initData() {

    const orderitem = this.custermizerService.getOrderItemData();
		this.engravingFirst = orderitem?.Engraving;
		this.engravingRepeat = orderitem?.Engraving;
		this.engravingError = this.engravingService.engravingError();
		this.fetchEngravingPossible();
	}

	onChanges(changes: SimpleChanges): void {
	}

	onUpdateEngraving(engraving: string) {
		this.engravingFirst = engraving.toUpperCase();
		this.engravingService.engraving = this.engravingFirst;
		this.checkEngraving();
	}

	onUpdateEngravingRepeat(engraving: string) {
		this.engravingRepeat = engraving.toUpperCase();
		this.engravingService.engravingRepeat = this.engravingRepeat;
		this.checkEngraving();
	}

	trimEngraving(engraving: string | undefined, isRepeat: boolean) {
		const trimmedEngraving = (engraving ?? "").trim();

		if (isRepeat) {
			this.engravingRepeat = trimmedEngraving;
			this.engravingService.engravingRepeat = trimmedEngraving;
		} else {
			this.engravingFirst = trimmedEngraving;
			this.engravingService.engraving = trimmedEngraving;
		}
		this.checkEngraving();
	}

	checkEngraving() {
		this.engravingError = this.engravingService.engravingError();
		this.menu.error = this.engravingError.length > 0;

		if (!this.engravingError) {
			this.events.showLoader(true);
			this.updateEngraving(this.engravingService.engraving);

			if (this.state == this.customizerStateEnum.EDITORDER) {
				this.showOverlay = true;
				new Promise(resolve => setTimeout(resolve, 2500)).then(x => {
					this.showOverlay = false;
				});
			}
		}
	}

	fetchEngravingPossible() {
		const chosenTemple = this.getVariant();
		this.engravingPossible = chosenTemple?.EngravingPossible;
	}

	private getVariant(): ComponentVariantModel | undefined {
		return this.variants.find((obj) => obj.Component.Code == ComponentCodeConstants.Temple);
	}

}
